import axios from 'axios';
import type { AxiosResponse } from 'axios';

export type DownloadResource = {
  fileName?: string;
  pdfLink: string;
};

export type DownloadBatchFn<TData = Blob | ArrayBuffer> = (pdfLink: string) => Promise<AxiosResponse<TData>>;

export type FailedDetail = { fileName: string; reason?: string };

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const getFilenameFromContentDisposition = (contentDisposition?: string): string | null => {
  if (!contentDisposition) return null;

  const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    const value = filenameStarMatch[1].trim();
    const parts = value.split("''");
    const encoded = (parts.length === 2 ? parts[1] : value).replace(/^"|"$/g, '');
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*("?)([^";]+)\1/i);
  return filenameMatch?.[2]?.trim() ?? null;
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const getErrorMessageFromData = (data: unknown): string | undefined => {
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object' && 'message' in data) {
    const msg = (data as { message?: unknown }).message;
    return typeof msg === 'string' ? msg : undefined;
  }
  return undefined;
};

const isRetryableDownloadError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;

  if (!error.response) return true; // Netzwerk/CORS/Timeout etc.

  const status = error.response.status;

  if (status === 404) return false; // definitiv kein Retry

  return status === 429 || status === 408 || status >= 500;
};

const downloadWithRetry = async <TResponse>(
  downloadBatch: (pdfLink: string) => Promise<TResponse>,
  pdfLink: string,
  opts?: { retries?: number; baseDelayMs?: number; perAttemptDelayMs?: number }
) => {
  const retries = opts?.retries ?? 2;
  const baseDelayMs = opts?.baseDelayMs ?? 350;
  const perAttemptDelayMs = opts?.perAttemptDelayMs ?? 150;

  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (perAttemptDelayMs > 0) await sleep(perAttemptDelayMs);

    try {
      return await downloadBatch(pdfLink);
    } catch (e) {
      lastError = e;

      const retryable = isRetryableDownloadError(e);
      const isLastAttempt = attempt >= retries;

      if (!retryable || isLastAttempt) throw e;

      const backoff = baseDelayMs * Math.pow(2, attempt);
      await sleep(backoff);
    }
  }

  throw lastError;
};

export async function runBatchDownloads(opts: {
  resources: DownloadResource[];
  downloadBatch: DownloadBatchFn;
  t: TFunction;
  retry?: { retries?: number; baseDelayMs?: number; perAttemptDelayMs?: number };
}): Promise<{ ok: string[]; failedDetails: FailedDetail[] }> {
  const { resources, downloadBatch, t, retry } = opts;

  const ok: string[] = [];
  const failedDetails: FailedDetail[] = [];

  for (let i = 0; i < resources.length; i += 1) {
    const { fileName, pdfLink } = resources[i];

    try {
      const response = await downloadWithRetry(downloadBatch, pdfLink, retry);

      const contentType = (response.headers?.['content-type'] as string | undefined) ?? 'application/pdf';
      const cd = response.headers?.['content-disposition'] as string | undefined;

      const resolvedName = getFilenameFromContentDisposition(cd) || fileName || 'download.pdf';
      const blob = new Blob([response.data], { type: contentType });

      triggerBlobDownload(blob, resolvedName);
      ok.push(resolvedName);
    } catch (error: unknown) {
      const name = fileName || 'download.pdf';

      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const backendMsg = axios.isAxiosError(error) ? getErrorMessageFromData(error.response?.data) : undefined;
      const genericMsg = error instanceof Error ? error.message : undefined;

      const reason = status === 404 ? t('batches.download.documentNotFound') : backendMsg || genericMsg || undefined;

      failedDetails.push({ fileName: name, reason });
    }
  }

  return { ok, failedDetails };
}
